export type GameplayEventAttribution = "verified" | "inferred" | "unknown";

export interface GameplayEvent {
  id: string;
  eventId: string;
  serverId: string;
  schemaVersion: number;
  source: string;
  type: string;
  category: string;
  attribution: GameplayEventAttribution;
  occurredAt: Date;
  ingestedAt: Date;
  actorSteam64Id?: string;
  actorName?: string;
  targetSteam64Id?: string;
  targetName?: string;
  groupId?: number;
  latitude?: number;
  longitude?: number;
  sensitive: boolean;
  payload?: Record<string, unknown>;
}

export interface GameplayEventQuery {
  serverId: string;
  limit: number;
  category?: string;
  type?: string;
  types?: string[];
  attribution?: GameplayEventAttribution;
  playerQuery?: string;
  playerSteam64Ids?: string[];
  source?: string;
  from?: Date;
  to?: Date;
  before?: { occurredAt: Date; id: string };
  excludeRoutineTelemetryCommands?: boolean;
}

export interface GameplayEventGroupMember {
  steam64Id: string;
  name?: string;
  species?: string;
}

export interface GameplayEventPresentationInput {
  type: string;
  attribution: GameplayEventAttribution;
  actorName?: string;
  targetName?: string;
  payload?: Record<string, unknown>;
}

/**
 * One canonical, non-technical description for gameplay evidence. It never
 * promotes an ID to a player name and never invents attribution the source did
 * not provide.
 */
export function describeGameplayEvent(event: GameplayEventPresentationInput): string {
  const actor = safePlayerName(event.actorName);
  const target = safePlayerName(event.targetName);
  const payload = event.payload ?? {};
  const members = gameplayEventGroupMembers(payload).map((member) => safePlayerName(member.name)).filter((name) => name !== "Unknown player");
  const groupedWith = humanList(members);
  switch (event.type) {
    case "player.damage": {
      const amount = typeof payload.damage === "number" ? ` for ${formatDamage(payload.damage)} damage` : "";
      const lethal = payload.lethal === true ? " (lethal)" : "";
      const cause = typeof payload.cause === "string" && payload.cause.trim() ? ` from ${payload.cause.trim()}` : "";
      if (payload.mechanism === "environmental") return `${target} took verified environmental damage${amount}${cause}${lethal}`;
      if (payload.mechanism === "dot") {
        return actor !== "Unknown player"
          ? `${actor}'s damage-over-time effect damaged ${target}${amount}${cause}${lethal}`
          : `${target} took verified damage over time${amount}${cause}${lethal}`;
      }
      if (actor !== "Unknown player" && target !== "Unknown player") return `${actor} damaged ${target}${amount}${lethal}`;
      if (target !== "Unknown player") return `${target} took verified player damage${amount}${lethal}`;
      return `Verified player damage recorded${amount}${lethal}`;
    }
    case "player.kill": {
      const mechanism = payload.mechanism === "dot" ? " with a damage-over-time effect" : "";
      return actor !== "Unknown player" && target !== "Unknown player"
        ? `${actor} killed ${target}${mechanism}`
        : `${target} died — killer not recorded by the source`;
    }
    case "player.death": {
      const cause = typeof payload.cause === "string" && payload.cause.trim() ? ` from ${payload.cause.trim()}` : "";
      const player = target !== "Unknown player" ? target : actor;
      return event.attribution === "verified" && payload.damageFeedSource === "native_gameplay_effect"
        ? `${player} died${cause} — no killer was recorded by the authoritative source`
        : `${player} died — killer not recorded by the source`;
    }
    case "player.chat": return `${actor}: ${String(payload.message ?? "Chat message recorded")}`;
    case "player.session.connected": return `${actor} joined the server`;
    case "player.session.safe_logout_prepared": return `${actor} started a safe logout`;
    case "player.session.disconnected": {
      if (payload.classification === "safe_logout") return `${actor} safely logged out`;
      if (payload.classification === "combat_logout") return `${actor} disconnected within 60 seconds of verified player combat`;
      if (payload.classification === "unplanned_disconnect") return `${actor} disconnected without completing a safe logout`;
      return `${actor} left the server — logout type was not captured`;
    }
    case "player.spawned": return `${actor} spawned as ${String(payload.species ?? "a dinosaur")}`;
    case "player.species.changed": return `${actor} changed from ${String(payload.previousSpecies ?? "an unknown species")} to ${String(payload.species ?? "an unknown species")}`;
    case "group.joined": return groupedWith ? `${actor} joined a group with ${groupedWith}` : `${actor} joined a group — other members were not captured`;
    case "group.left": return groupedWith ? `${actor} left a group containing ${groupedWith}` : `${actor} left a group — other members were not captured`;
    case "group.changed": return groupedWith ? `${actor} moved to a group with ${groupedWith}` : `${actor} changed groups — other members were not captured`;
    case "server.admin_command": return actor === "Unknown player"
      ? `Admin command used: ${String(payload.command ?? "command not captured")}`
      : `${actor} used ${String(payload.command ?? "a game-server command")}`;
    default: return `${actor} — ${event.type.replaceAll(/[._-]+/g, " ")}`;
  }
}

function formatDamage(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

export function gameplayEventGroupMembers(payload: Record<string, unknown> | undefined): GameplayEventGroupMember[] {
  const value = payload?.groupMembers;
  if (!Array.isArray(value)) return [];
  return value.flatMap((member) => {
    if (!member || typeof member !== "object") return [];
    const row = member as Record<string, unknown>;
    if (typeof row.steam64Id !== "string" || !/^\d{17}$/.test(row.steam64Id)) return [];
    return [{
      steam64Id: row.steam64Id,
      ...(typeof row.name === "string" ? { name: row.name } : {}),
      ...(typeof row.species === "string" ? { species: row.species } : {})
    }];
  });
}

function safePlayerName(value: string | undefined): string {
  const candidate = value?.trim();
  if (!candidate || /^\d{17}$/.test(candidate) || /^steam\s+\d{17}$/i.test(candidate) || /^usr_/i.test(candidate) || /^unknown(?:\s+player)?$/i.test(candidate)) return "Unknown player";
  return candidate;
}

function humanList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

export function isRoutineTelemetryCommand(command: string): boolean {
  const trimmed = command.trim();
  if (/^used\s*\[\s*\]\s*:\s*7656\d{13}\s*$/i.test(trimmed)) return true;
  const normalized = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return normalized === "get player list"
    || normalized === "getplayerlist"
    || normalized === "used get player list"
    || normalized.startsWith("used get player list ")
    || normalized.startsWith("get player data ")
    || normalized.startsWith("getplayerdata ")
    || normalized.startsWith("used get player data ")
    || normalized.startsWith("used getplayerdata ")
    || normalized.startsWith("adjust ai density ")
    || normalized.startsWith("adjustaidensity ")
    || normalized.startsWith("used adjust ai density ")
    || normalized.startsWith("used adjustaidensity ");
}

export interface TelemetryHealthSample {
  id: string;
  serverId: string;
  source: string;
  status: "healthy" | "degraded" | "failed";
  schemaVersion: number;
  bootId?: string;
  sequence?: number;
  capturedAt?: Date;
  ingestedAt: Date;
  persistedAt?: Date;
  captureToIngestMs?: number;
  ingestToPersistMs?: number;
  collectorDurationMs?: number;
  recordCount: number;
  duplicateCount: number;
  invalidCount: number;
  missingSequenceCount: number;
  error?: string;
  metadata?: Record<string, unknown>;
}

export interface TelemetryRetentionPolicy {
  serverId: string;
  preciseLocationDays: number;
  chatEventDays: number;
  gameplayEventDays: number;
  telemetryHealthDays: number;
  updatedByUserId: string;
  updatedAt: Date;
}

export const defaultTelemetryRetentionPolicy = (
  serverId: string,
  updatedByUserId = "system",
  now = new Date()
): TelemetryRetentionPolicy => ({
  serverId,
  preciseLocationDays: 7,
  chatEventDays: 30,
  gameplayEventDays: 180,
  telemetryHealthDays: 30,
  updatedByUserId,
  updatedAt: now
});
